/*
 * Dorellini PDP Subscription Selector
 * -------------------------------------------------------------------------
 * Vanilla custom element (no @theme imports) so it is decoupled from Horizon
 * internals and safe to drop into a custom block.
 *
 * Responsibilities:
 *   1. Wire One-time / Subscribe modes, box-quantity controls and the frequency
 *      <select> to the theme's existing product <form>.
 *   2. Subscribe -> write the chosen frequency's selling_plan id into a hidden
 *      input[name="selling_plan"][data-dor-plan] that lives as a DIRECT CHILD
 *      of <form> (outside .product-form-buttons, so Horizon's morph can't wipe
 *      it). Quantity is driven through the theme's input[name="quantity"].
 *   3. One-time -> disable/clear our input so no selling_plan is submitted.
 *   4. GUARANTEE OUR PLAN WINS: neutralize any OTHER selling_plan field that a
 *      leftover Appstle widget injects into the form (strip its name).
 *
 * QUANTITY (subscribe mode): the counter [- N +] is the single source of truth.
 * The 1 / 2 / 3+ tier pills are shortcuts that set the counter to 1 / 2 / 3.
 * For qty >= 3 the "3+" pill stays highlighted (Bundles applies 15% to all
 * qty >= 3 at the cart layer). The counter writes into the theme's quantity
 * input — the exact same mechanism the pills always used.
 *
 * NOTE: the selling_plan / frequency wiring is unchanged from the prior version.
 */
(function () {
  if (customElements.get('dor-subscription-selector')) return;

  class DorSubscriptionSelector extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.mode = this.dataset.defaultMode === 'subscribe' ? 'subscribe' : 'onetime';
      this.qty = 1;
      this.maxQty = parseInt(this.dataset.qtyMax, 10) || 20;

      // Display data for the live per-box summary (formatted strings + save %).
      this.prices = { 1: this.dataset.price1 || '', 2: this.dataset.price2 || '', 3: this.dataset.price3 || '' };
      this.saves = { 1: this.dataset.save1 || '', 2: this.dataset.save2 || '', 3: this.dataset.save3 || '' };

      this.modeButtons = Array.from(this.querySelectorAll('[data-mode]'));
      this.subPanel = this.querySelector('[data-panel="subscribe"]');
      this.qtyButtons = Array.from(this.querySelectorAll('[data-qty]'));
      this.stepButtons = Array.from(this.querySelectorAll('[data-step]'));
      this.countEl = this.querySelector('[data-count]');
      this.summaryEl = this.querySelector('[data-summary]');
      this.freqSelect = this.querySelector('[data-freq]');
      this.planId = this.freqSelect ? this.freqSelect.value : this.dataset.planDefault || '';

      this.modeButtons.forEach((b) =>
        b.addEventListener('click', () => this.setMode(b.dataset.mode))
      );
      // Tier pills -> jump the counter to 1 / 2 / 3.
      this.qtyButtons.forEach((b) =>
        b.addEventListener('click', () => this.setQuantity(parseInt(b.dataset.qty, 10) || 1))
      );
      // Counter +/- -> step the quantity.
      this.stepButtons.forEach((b) =>
        b.addEventListener('click', () => this.setQuantity(this.qty + (parseInt(b.dataset.step, 10) || 0)))
      );
      // Typeable quantity field (iOS numeric keypad comes from inputmode in markup).
      if (this.countEl && this.countEl.tagName === 'INPUT') {
        this.countEl.addEventListener('input', () => this.onCountInput());
        this.countEl.addEventListener('blur', () => this.onCountBlur());
        this.countEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this.countEl.blur(); }
        });
      }
      if (this.freqSelect) {
        this.freqSelect.addEventListener('change', () => {
          this.planId = this.freqSelect.value;
          if (this.mode === 'subscribe') this.syncForm();
        });
      }

      // Defensive: strip any foreign selling_plan field BEFORE Horizon builds
      // FormData. Capture phase => runs before the form's own submit handler.
      this._onSubmitCapture = (e) => {
        const form = this.form;
        if (form && (e.target === form || form.contains(e.target))) {
          this.neutralizeForeignPlans();
        }
      };
      document.addEventListener('submit', this._onSubmitCapture, true);

      this._observer = new MutationObserver(() => this.neutralizeForeignPlans());
      const watchRoot = this.section instanceof Element ? this.section : document.body;
      this._observer.observe(watchRoot, { childList: true, subtree: true });

      this.setMode(this.mode, true);

      this._sweeps = [400, 1500, 3500].map((t) =>
        setTimeout(() => this.neutralizeForeignPlans(), t)
      );
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._onSubmitCapture, true);
      this._observer && this._observer.disconnect();
      (this._sweeps || []).forEach(clearTimeout);
    }

    get form() {
      return (
        this.section.querySelector('product-form-component form') ||
        document.querySelector('product-form-component form')
      );
    }

    get qtyInput() {
      const form = this.form;
      return form ? form.querySelector('input[name="quantity"]') : null;
    }

    get qtyWrapper() {
      const input = this.qtyInput;
      return input ? input.closest('.quantity-selector-wrapper') : null;
    }

    ensurePlanInput() {
      const form = this.form;
      if (!form) return null;
      let input = form.querySelector('input[name="selling_plan"][data-dor-plan]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.setAttribute('data-dor-plan', '');
        const idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.parentElement === form) {
          idInput.insertAdjacentElement('afterend', input);
        } else {
          form.prepend(input);
        }
      }
      return input;
    }

    neutralizeForeignPlans() {
      const form = this.form;
      if (!form) return;
      form.querySelectorAll('[name="selling_plan"]').forEach((el) => {
        if (el.hasAttribute('data-dor-plan')) return; // ours — leave it
        el.setAttribute('data-dor-disabled-name', el.getAttribute('name') || '');
        el.removeAttribute('name');
        el.disabled = true;
      });
    }

    setMode(mode, isInit) {
      this.mode = mode === 'subscribe' ? 'subscribe' : 'onetime';
      this.modeButtons.forEach((b) => {
        const active = b.dataset.mode === this.mode;
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
        b.classList.toggle('is-selected', active);
      });
      if (this.subPanel) this.subPanel.hidden = this.mode !== 'subscribe';

      this.syncForm(isInit);

      if (this.mode === 'subscribe') {
        // Drives the counter display + the theme quantity input.
        this.setQuantity(this.qty || 1);
      } else {
        // Keep the counter display in sync (it is hidden in one-time mode).
        this.highlightQty();
        this.updateCounter();
        this.updateSummary();
      }
    }

    /** Single source of truth for quantity. Clamps, updates UI, drives the form. */
    setQuantity(n, opts) {
      opts = opts || {};
      let q = parseInt(n, 10);
      if (isNaN(q)) q = 1;
      if (q < 1) q = 1;
      if (q > this.maxQty) q = this.maxQty;
      this.qty = q;
      this.highlightQty();
      if (!opts.skipCounter) this.updateCounter();
      this.updateSummary();
      if (this.mode === 'subscribe') this.applyQtyToForm();
    }

    /** Live typing: keep only digits and update everything via the same path as
     *  the +/- buttons, without rewriting the field mid-type (except clamping > max). */
    onCountInput() {
      const el = this.countEl;
      const digits = el.value.replace(/[^0-9]/g, '');
      if (digits !== el.value) el.value = digits;
      if (digits === '') return; // allow empty while typing; clamp on blur
      let q = parseInt(digits, 10);
      if (q > this.maxQty) { q = this.maxQty; el.value = String(q); }
      this.setQuantity(q, { skipCounter: true });
    }

    /** On blur, normalize the field to a clamped integer (min 1, max maxQty). */
    onCountBlur() {
      let q = parseInt(this.countEl.value, 10);
      if (isNaN(q) || q < 1) q = 1;
      if (q > this.maxQty) q = this.maxQty;
      this.setQuantity(q);
    }

    highlightQty() {
      this.qtyButtons.forEach((b) => {
        const pillQ = parseInt(b.dataset.qty, 10);
        // qty >= 3 keeps the "3+" pill active (15% applies to all qty >= 3).
        const active = (this.qty >= 3 && pillQ === 3) || pillQ === this.qty;
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
        b.classList.toggle('is-selected', active);
      });
    }

    updateCounter() {
      if (!this.countEl) return;
      if (this.countEl.tagName === 'INPUT') this.countEl.value = String(this.qty);
      else this.countEl.textContent = String(this.qty);
    }

    updateSummary() {
      if (!this.summaryEl) return;
      const tier = this.qty >= 3 ? 3 : this.qty;
      const price = this.prices[tier] || '';
      const save = this.saves[tier] || '';
      const boxes = this.qty === 1 ? 'box' : 'boxes';
      // Colored parts: price espresso, "/box" muted, "save X%" terracotta.
      this.summaryEl.innerHTML =
        `${this.qty} ${boxes} · ${price}<span class="dor-sub__per">/box</span>` +
        ` · <span class="dor-sub__sum-save">save ${save}%</span>`;
    }

    applyQtyToForm() {
      const input = this.qtyInput;
      if (!input) return;
      input.value = String(this.qty);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      const comp = input.closest('quantity-selector-component');
      if (comp && typeof comp.setValue === 'function') {
        try { comp.setValue(this.qty); } catch (e) { /* noop */ }
      }
    }

    syncForm(isInit) {
      const planInput = this.ensurePlanInput();
      const wrapper = this.qtyWrapper;

      this.neutralizeForeignPlans();

      if (this.mode === 'subscribe') {
        if (planInput) {
          planInput.value = this.planId || '';
          planInput.disabled = false;
        }
        // Subscribe uses our counter; hide the theme's numeric stepper.
        // Inline display:none beats the theme's `.quantity-selector-wrapper{display:flex}`
        // (the [hidden] attribute does not). The input[name="quantity"] stays in the
        // DOM and still receives our counter's value via applyQtyToForm().
        if (wrapper) wrapper.style.display = 'none';
      } else {
        if (planInput) {
          planInput.value = '';
          planInput.disabled = true;
        }
        if (wrapper) wrapper.style.display = '';
      }
    }
  }

  customElements.define('dor-subscription-selector', DorSubscriptionSelector);
})();
